import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useServiceCategories } from '../../context/ServiceCategoryContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  CalendarIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const ServiceCategoryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { serviceCategories, getServiceCategories } = useServiceCategories();
  const { hideLoading } = useLoading();
  const getRoutePath = useRoutePath();

  const [category, setCategory] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  const hasFetchedCategory = useRef(false);

  const fetchCategory = useCallback(
    async () => {
      if (!id || hasFetchedCategory.current) return;

      hasFetchedCategory.current = true;
      setIsFetching(true);

      try {
        // Load all categories (includes parent relation) – same pattern as CustomerView
        const response = await getServiceCategories(1, { all: true });

        let found = null;
        if (response?.data) {
          const list = Array.isArray(response.data)
            ? response.data
            : response.data?.data;
          if (Array.isArray(list)) {
            found = list.find(item => Number(item.id) === Number(id));
          }
        }

        if (found) {
          setCategory(found);
        } else {
          setCategory(null);
        }
      } catch (error) {
        console.error('Error fetching service category data:', error);
        setCategory(null);
      } finally {
        setIsFetching(false);
        hideLoading();
      }
    },
    [id, getServiceCategories, hideLoading]
  );

  useEffect(() => {
    // If list is already in context we might be able to resolve quickly
    if (id && serviceCategories?.data?.length && !category) {
      const existing = serviceCategories.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing) {
        setCategory(existing);
        setIsFetching(false);
        return;
      }
    }

    fetchCategory();
  }, [id, serviceCategories, category, fetchCategory]);

  const handleEdit = () => {
    navigate(getRoutePath(`/service-categories/edit/${id}`));
  };

  const getStatusBadge = () => {
    const isActive = category?.active === 1 || category?.active === true;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="h-4 w-4 mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };



  if (!isFetching && !category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Service Category Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The service category you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/service-categories'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Service Categories
          </button>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading service category information...</p>
        </div>
      </div>
    );
  }

  const isSubCategory = !!category.parent;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            {/* Left side */}
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <CalendarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{category?.name || 'Service Category Details'}</span>
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{getStatusBadge()}</span>
                  <span className="text-gray-500">
                    {isSubCategory ? 'Sub Category' : 'Main Category'}
                  </span>
                  {category.formated_created_at && (
                    <span className="text-gray-500">
                      Created: {category.formated_created_at}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/service-categories'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>

              <button
                onClick={handleEdit}
                className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Category
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="xl:col-span-2 space-y-6">
            {/* Category Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Service Category Information
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category Name
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {category.name || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent Category
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {category.parent?.name || 'None'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {isSubCategory ? 'Sub Category' : 'Main Category'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {getStatusBadge()}
                    </div>
                  </div>
                </div>

                {category.display_image?.path && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Image
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg inline-flex items-center">
                      <img
                        src={category.display_image.path}
                        alt={category.name}
                        className="h-16 w-16 rounded-full object-cover border border-gray-200"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            {/* Category Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Category Summary
                </h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Type</span>
                  <span className="font-medium text-gray-900">
                    {isSubCategory ? 'Sub Category' : 'Main Category'}
                  </span>
                </div>

                {category.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Created At
                    </div>
                    <div className="text-sm text-gray-900">
                      {category.formated_created_at}
                    </div>
                  </div>
                )}

                {category.formated_updated_at && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Last Updated
                    </div>
                    <div className="text-sm text-gray-900">
                      {category.formated_updated_at}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCategoryView;

